// src/services/subtasks.ts
import { supabase } from "../lib/supabase";
import type { ID, Subtask } from "../types/domain";

export async function listSubtasks(taskId: ID) {
  const { data, error } = await supabase
    .from("subtasks")
    .select("*")
    .eq("task_id", taskId)
    .order("position", { ascending: true, nullsFirst: true })
    .order("id", { ascending: true });

  return { data: (data as Subtask[]) ?? [], error };
}

export async function getNextSubtaskPosition(taskId: ID) {
  const { data } = await supabase
    .from("subtasks")
    .select("position")
    .eq("task_id", taskId)
    .not("position", "is", null)
    .order("position", { ascending: false })
    .limit(1);
  const last = (data && (data as any[])[0]?.position) ?? 0;
  return (Number.isFinite(last) ? Number(last) : 0) + 1;
}

export async function createSubtask(payload: { task_id: ID; title: string; position?: number | null }) {
  const position = typeof payload.position === "number" ? payload.position : await getNextSubtaskPosition(payload.task_id);
  const { data, error } = await supabase
    .from("subtasks")
    .insert({ task_id: payload.task_id, title: payload.title, done: false, position })
    .select()
    .single();

  return { data: data as Subtask | null, error };
}

export async function updateSubtask(id: ID, patch: Partial<Subtask>) {
  const { data, error } = await supabase
    .from("subtasks")
    .update({
      title: patch.title,
      done: typeof patch.done === "boolean" ? patch.done : undefined,
      position: typeof patch.position === "number" ? patch.position : undefined,
    })
    .eq("id", id)
    .select()
    .single();

  return { data: data as Subtask | null, error };
}

export async function deleteSubtask(id: ID) {
  const { error } = await supabase.from("subtasks").delete().eq("id", id);
  return { error };
}

export async function reorderSubtasks(taskId: ID, ordered: ID[]) {
  if (!ordered.length) return { error: null };
  const updates = ordered.map((id, idx) => ({ id, task_id: taskId, position: idx + 1 }));
  const { error } = await supabase.from("subtasks").upsert(updates, { onConflict: "id" });
  return { error };
}

export async function getSubtaskStatsForTasks(taskIds: ID[]) {
  if (!taskIds.length) return new Map<ID, { total: number; done: number }>();
  const { data, error } = await supabase
    .from("subtasks")
    .select("task_id, done")
    .in("task_id", taskIds);
  if (error) return new Map<ID, { total: number; done: number }>();

  const map = new Map<ID, { total: number; done: number }>();
  (data as { task_id: ID; done: boolean }[]).forEach((row) => {
    const cur = map.get(row.task_id) ?? { total: 0, done: 0 };
    cur.total += 1;
    if (row.done) cur.done += 1;
    map.set(row.task_id, cur);
  });
  return map;
}
